/**
 * Namespaced storage for org legal & compliance wizard fields inside
 * `Organization.privateMetadata` (JSON).
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();export const LEGAL_COMPLIANCE_PRIVATE_METADATA_KEY = "legalCompliance" as const

export type OrgLegalComplianceInsuranceOption = "individual" | "organizational"

/** Payload stored under `legalCompliance` (versioned for forward compatibility). */
export type OrgLegalCompliancePrivateMetadataV1 = {
  v: 1
  legalName?: string
  dbaName?: string
  stateOfOrganization?: string
  authorizedSignerName?: string
  authorizedSignerTitle?: string
  authorizedSignerEmail?: string
  businessNoticeAddressLine1?: string
  businessNoticeAddressLine2?: string
  businessNoticeAddressCity?: string
  businessNoticeAddressState?: string
  businessNoticeAddressPostalCode?: string
  businessNoticeAddressCountry?: string
  privacyContactName?: string
  privacyContactTitle?: string
  privacyContactEmail?: string
  privacySecuritySamePerson?: boolean
  securityContactName?: string
  securityContactTitle?: string
  securityContactEmail?: string
  /** @deprecated legacy fields kept for backward compatibility when parsing */
  npi?: string
  /** @deprecated legacy fields kept for backward compatibility when parsing */
  businessContactName?: string
  /** @deprecated legacy fields kept for backward compatibility when parsing */
  businessContactTitle?: string
  electronicSignatureAgreement?: boolean
  authorizedSignerConfirmed?: boolean
  baaAccepted?: boolean
  insuranceOption?: OrgLegalComplianceInsuranceOption
  /** When a file was uploaded client-side; binary is not stored in JSON. */
  insuranceDocumentFileName?: string | null
}

/** Partial form fields derived only from private metadata (plus optional hydration helpers). */
export type OrgLegalComplianceParsed = {
  legalName?: string
  dbaName?: string
  stateOfOrganization?: string
  authorizedSignerName?: string
  authorizedSignerTitle?: string
  authorizedSignerEmail?: string
  businessNoticeAddressLine1?: string
  businessNoticeAddressLine2?: string
  businessNoticeAddressCity?: string
  businessNoticeAddressState?: string
  businessNoticeAddressPostalCode?: string
  businessNoticeAddressCountry?: string
  privacyContactName?: string
  privacyContactTitle?: string
  privacyContactEmail?: string
  privacySecuritySamePerson?: boolean
  securityContactName?: string
  securityContactTitle?: string
  securityContactEmail?: string
  npi?: string
  businessContactName?: string
  businessContactTitle?: string
  electronicSignatureAgreement?: boolean
  authorizedSignerConfirmed?: boolean
  baaAccepted?: boolean
  insuranceOption?: OrgLegalComplianceInsuranceOption
  insuranceDocumentFileName?: string | null
}

function parseJsonObject(raw: unknown): Record<string, unknown> {
  if (raw == null) {
    return {}
  }
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as unknown
      return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {}
    } catch {
      return {}
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>
  }
  return {}
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

function isInsuranceOption(
  value: unknown,
): value is OrgLegalComplianceInsuranceOption {
  return value === "individual" || value === "organizational"
}

/**
 * Reads the `legalCompliance` namespace from organization `privateMetadata`.
 */
export function parseOrgLegalCompliancePrivateMetadata(
  rawPrivateMetadata: unknown,
): OrgLegalComplianceParsed {
  const root = parseJsonObject(rawPrivateMetadata)
  const block = root[LEGAL_COMPLIANCE_PRIVATE_METADATA_KEY]
  if (!isRecord(block)) {
    return {}
  }
  if (block.v !== 1 && block.v !== undefined) {
    return {}
  }

  const out: OrgLegalComplianceParsed = {}

  if (typeof block.legalName === "string") {
    out.legalName = block.legalName
  }
  if (typeof block.dbaName === "string") {
    out.dbaName = block.dbaName
  }
  if (typeof block.stateOfOrganization === "string") {
    out.stateOfOrganization = block.stateOfOrganization
  }
  if (typeof block.authorizedSignerName === "string") {
    out.authorizedSignerName = block.authorizedSignerName
  }
  if (typeof block.authorizedSignerTitle === "string") {
    out.authorizedSignerTitle = block.authorizedSignerTitle
  }
  if (typeof block.authorizedSignerEmail === "string") {
    out.authorizedSignerEmail = block.authorizedSignerEmail
  }
  if (typeof block.businessNoticeAddressLine1 === "string") {
    out.businessNoticeAddressLine1 = block.businessNoticeAddressLine1
  }
  if (typeof block.businessNoticeAddressLine2 === "string") {
    out.businessNoticeAddressLine2 = block.businessNoticeAddressLine2
  }
  if (typeof block.businessNoticeAddressCity === "string") {
    out.businessNoticeAddressCity = block.businessNoticeAddressCity
  }
  if (typeof block.businessNoticeAddressState === "string") {
    out.businessNoticeAddressState = block.businessNoticeAddressState
  }
  if (typeof block.businessNoticeAddressPostalCode === "string") {
    out.businessNoticeAddressPostalCode = block.businessNoticeAddressPostalCode
  }
  if (typeof block.businessNoticeAddressCountry === "string") {
    out.businessNoticeAddressCountry = block.businessNoticeAddressCountry
  }
  if (typeof block.privacyContactName === "string") {
    out.privacyContactName = block.privacyContactName
  }
  if (typeof block.privacyContactTitle === "string") {
    out.privacyContactTitle = block.privacyContactTitle
  }
  if (typeof block.privacyContactEmail === "string") {
    out.privacyContactEmail = block.privacyContactEmail
  }
  if (typeof block.privacySecuritySamePerson === "boolean") {
    out.privacySecuritySamePerson = block.privacySecuritySamePerson
  }
  if (typeof block.securityContactName === "string") {
    out.securityContactName = block.securityContactName
  }
  if (typeof block.securityContactTitle === "string") {
    out.securityContactTitle = block.securityContactTitle
  }
  if (typeof block.securityContactEmail === "string") {
    out.securityContactEmail = block.securityContactEmail
  }
  if (typeof block.npi === "string") {
    out.npi = block.npi
  }
  if (typeof block.businessContactName === "string") {
    out.businessContactName = block.businessContactName
  }
  if (typeof block.businessContactTitle === "string") {
    out.businessContactTitle = block.businessContactTitle
  }
  if (typeof block.electronicSignatureAgreement === "boolean") {
    out.electronicSignatureAgreement = block.electronicSignatureAgreement
  }
  if (typeof block.authorizedSignerConfirmed === "boolean") {
    out.authorizedSignerConfirmed = block.authorizedSignerConfirmed
  }
  if (typeof block.baaAccepted === "boolean") {
    out.baaAccepted = block.baaAccepted
  }
  if (isInsuranceOption(block.insuranceOption)) {
    out.insuranceOption = block.insuranceOption
  }
  if (block.insuranceDocumentFileName === null) {
    out.insuranceDocumentFileName = null
  } else if (typeof block.insuranceDocumentFileName === "string") {
    out.insuranceDocumentFileName = block.insuranceDocumentFileName
  }

  return out
}

export type OrgLegalComplianceFormLike = {
  legalName?: string
  dbaName?: string
  stateOfOrganization?: string
  businessNoticeAddress?: {
    line1?: string
    line2?: string
    city?: string
    state?: string
    postalCode?: string
    country?: string
  }
  privacyContact?: {
    name?: string
    title?: string
    email?: string
  }
  privacySecuritySamePerson?: boolean
  securityContact?: {
    name?: string
    title?: string
    email?: string
  }
  /** @deprecated legacy field */
  npi?: string
  /** @deprecated legacy field */
  businessContact?: {
    name?: string
    title?: string
    email?: string
    phone?: string
  }
  electronicSignatureAgreement?: boolean
  authorizedSignerConfirmed?: boolean
  baaAccepted?: boolean
  insuranceOption?: OrgLegalComplianceInsuranceOption
  /** File name only; used when serializing (File object not JSON-safe). */
  insuranceDocumentFileName?: string | null
}

/**
 * Builds the patch object `{ legalCompliance: { v: 1, ... } }` for merging into
 * full `privateMetadata`.
 */
export function formatOrgLegalCompliancePrivateMetadataPatch(
  form: OrgLegalComplianceFormLike,
): Record<string, OrgLegalCompliancePrivateMetadataV1> {
  const payload: OrgLegalCompliancePrivateMetadataV1 = { v: 1 }

  if (form.legalName != null && form.legalName !== "") {
    payload.legalName = form.legalName
  }
  if (form.dbaName != null && form.dbaName !== "") {
    payload.dbaName = form.dbaName
  }
  if (form.stateOfOrganization != null && form.stateOfOrganization !== "") {
    payload.stateOfOrganization = form.stateOfOrganization
  }
  if (
    form.businessNoticeAddress?.line1 != null &&
    form.businessNoticeAddress.line1 !== ""
  ) {
    payload.businessNoticeAddressLine1 = form.businessNoticeAddress.line1
  }
  if (
    form.businessNoticeAddress?.line2 != null &&
    form.businessNoticeAddress.line2 !== ""
  ) {
    payload.businessNoticeAddressLine2 = form.businessNoticeAddress.line2
  }
  if (
    form.businessNoticeAddress?.city != null &&
    form.businessNoticeAddress.city !== ""
  ) {
    payload.businessNoticeAddressCity = form.businessNoticeAddress.city
  }
  if (
    form.businessNoticeAddress?.state != null &&
    form.businessNoticeAddress.state !== ""
  ) {
    payload.businessNoticeAddressState = form.businessNoticeAddress.state
  }
  if (
    form.businessNoticeAddress?.postalCode != null &&
    form.businessNoticeAddress.postalCode !== ""
  ) {
    payload.businessNoticeAddressPostalCode = form.businessNoticeAddress.postalCode
  }
  if (
    form.businessNoticeAddress?.country != null &&
    form.businessNoticeAddress.country !== ""
  ) {
    payload.businessNoticeAddressCountry = form.businessNoticeAddress.country
  }
  if (form.privacyContact?.name != null && form.privacyContact.name !== "") {
    payload.privacyContactName = form.privacyContact.name
  }
  if (form.privacyContact?.title != null && form.privacyContact.title !== "") {
    payload.privacyContactTitle = form.privacyContact.title
  }
  if (form.privacyContact?.email != null && form.privacyContact.email !== "") {
    payload.privacyContactEmail = form.privacyContact.email
  }
  if (typeof form.privacySecuritySamePerson === "boolean") {
    payload.privacySecuritySamePerson = form.privacySecuritySamePerson
  }
  if (form.securityContact?.name != null && form.securityContact.name !== "") {
    payload.securityContactName = form.securityContact.name
  }
  if (form.securityContact?.title != null && form.securityContact.title !== "") {
    payload.securityContactTitle = form.securityContact.title
  }
  if (form.securityContact?.email != null && form.securityContact.email !== "") {
    payload.securityContactEmail = form.securityContact.email
  }
  if (form.npi != null && form.npi !== "") {
    payload.npi = form.npi
  }
  if (form.businessContact?.name != null && form.businessContact.name !== "") {
    payload.businessContactName = form.businessContact.name
  }
  if (form.businessContact?.title != null && form.businessContact.title !== "") {
    payload.businessContactTitle = form.businessContact.title
  }
  if (typeof form.electronicSignatureAgreement === "boolean") {
    payload.electronicSignatureAgreement = form.electronicSignatureAgreement
  }
  if (typeof form.authorizedSignerConfirmed === "boolean") {
    payload.authorizedSignerConfirmed = form.authorizedSignerConfirmed
  }
  if (typeof form.baaAccepted === "boolean") {
    payload.baaAccepted = form.baaAccepted
  }
  if (form.insuranceOption != null) {
    payload.insuranceOption = form.insuranceOption
  }
  if (form.insuranceDocumentFileName !== undefined) {
    payload.insuranceDocumentFileName = form.insuranceDocumentFileName
  }

  return { [LEGAL_COMPLIANCE_PRIVATE_METADATA_KEY]: payload }
}

/**
 * Deep-merge `legalCompliance` into existing private metadata without removing
 * unrelated top-level keys.
 */
export function mergeOrgLegalComplianceIntoPrivateMetadata(
  existingPrivateMetadata: unknown,
  patch: ReturnType<typeof formatOrgLegalCompliancePrivateMetadataPatch>,
): Record<string, unknown> {
  const root = { ...parseJsonObject(existingPrivateMetadata) }
  const key = LEGAL_COMPLIANCE_PRIVATE_METADATA_KEY
  root[key] = patch[key] as unknown as Record<string, unknown>
  return root
}
